import type React from 'react';
import { useEffect } from 'react';
import { safeSameOriginPath } from '@showliner/shared';
import { getAdminUrl, getPublicUrl } from '@/utils/crossAppLinks';

/**
 * Login lives on admin.showliner.io. This stub forwards to admin so we don't
 * maintain two parallel sign-in flows. Existing query params are preserved;
 * a relative `?redirect=` is upgraded to an absolute public URL so admin can
 * route the user back to the public site after authenticating.
 *
 * Route is preserved so existing links/bookmarks keep working.
 */
const Login: React.FC = () => {
  useEffect(() => {
    const dest = new URL(getAdminUrl('/login'));
    const incoming = new URLSearchParams(window.location.search);

    // Only a single-slash same-origin path is upgraded (issue #815). Anything
    // else is passed through as written and refused by admin's own check, so
    // `//evil.example` is never turned into an absolute Showliner URL here.
    const existingRedirect = safeSameOriginPath(incoming.get('redirect'));
    if (existingRedirect) {
      incoming.set('redirect', getPublicUrl(existingRedirect));
    }

    incoming.forEach((value, key) => {
      dest.searchParams.set(key, value);
    });
    window.location.replace(dest.toString());
  }, []);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <p className="text-sm text-grey-500">Redirecting to sign in…</p>
    </div>
  );
};

export default Login;
